import React, { useReducer, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { functionUrls } from './services/firebaseFunctions';
import ThinkingIndicator from './components/ThinkingIndicator';
import { parseHistoricalPeriodsMarkdown } from './utils/historicalPeriodsParser';
import { loginMachine } from './auth/loginMachine';

// Shared cache that survives component unmounts (one Map for the whole module)
const periodCache = new Map();

// Initial reducer state
const initialState = {
  status: 'idle',      // 'idle' | 'loading' | 'success' | 'error'
  periods: [],
  error: null,
  key: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return initialState;
    case 'SET_TOPIC': {
      const { key } = action.payload || {};
      return {
        status: 'idle',
        periods: [],
        error: null,
        key: key || null,
      };
    }
    case 'FETCH_START': {
      const { key, keepPeriods = false } = action.payload || {};
      return {
        ...state,
        status: 'loading',
        error: null,
        key: key || state.key,
        periods: keepPeriods ? state.periods : [],
      };
    }
    case 'FETCH_SUCCESS': {
      const { key, periods } = action.payload || {};
      return {
        ...state,
        status: 'success',
        periods: periods || [],
        error: null,
        key: key || state.key,
      };
    }
    case 'FETCH_ERROR': {
      const { key, error } = action.payload || {};
      return {
        ...state,
        status: 'error',
        periods: [],
        error,
        key: key || state.key,
      };
    }
    default:
      return state;
  }
}

// Helper to derive a human readable location name
function getLocationName(loc) {
  if (!loc) return '';
  const addr = loc?.address;
  const fromAddress = addr
    ? (
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.county ||
      addr.state ||
      addr.country ||
      ''
    )
    : '';

  return (
    loc?.topic_place_name ||
    fromAddress ||
    loc?.display_name ||
    addr?.display_name ||
    loc?.name ||
    ''
  );
}

function buildTopicKey(topicId, userLanguageLabel) {
  const baseKey = topicId || 'unknown-topic';
  // Include language so that caches do not mix different localisations
  return `${baseKey}|lang:${userLanguageLabel}`;
}

/**
 * HistoricalPeriods â€“ fetches and renders historical eras for the current topic.
 * Exposes an imperative `fetchPeriods` method to initiate the fetch on demand.
 */
const HistoricalPeriods = forwardRef(({
  topic,
  topicId,
  userLanguageLabel,
  startNarration,
  locationData,
  isGuestAccount = true,
  onSignInClick = () => {},
}, ref) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortRef = useRef(null);
  const topicKeyRef = useRef(buildTopicKey(topicId, userLanguageLabel));

  // Update key when topic changes.
  // Do NOT hard-reset UI immediately to avoid "disappearing" periods during
  // metadata-only updates (same place, richer payload). Keep previous periods
  // visible until cache/fetch for the new key is ready.
  useEffect(() => {
    const newKey = buildTopicKey(topicId, userLanguageLabel);
    if (newKey === topicKeyRef.current) return;

    topicKeyRef.current = newKey;

    // Abort any ongoing request tied to the previous topic
    abortRef.current?.abort?.();

    // Fast path: immediately hydrate from cache
    if (periodCache.has(newKey)) {
      dispatch({ type: 'FETCH_SUCCESS', payload: { key: newKey, periods: periodCache.get(newKey) } });
      return;
    }

    // No auto-fetch on topic switch. Keep component idle until user explicitly
    // clicks the Historical Eras button.
    dispatch({ type: 'SET_TOPIC', payload: { key: newKey } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, userLanguageLabel]);

  // Core fetch function (exposed imperatively)
  async function fetchPeriods(requestKey = topicKeyRef.current) {
    const currentKey = requestKey || topicKeyRef.current;

    // Use cached value when available
    if (periodCache.has(currentKey)) {
      dispatch({ type: 'FETCH_SUCCESS', payload: { key: currentKey, periods: periodCache.get(currentKey) } });
      return;
    }

    // Avoid duplicate calls while loading for the same topic key
    if (state.status === 'loading' && state.key === currentKey) return;

    const title = topic.type === 'article'
      ? topic.article?.title
      : (topic.locationData?.topic_place_name || getLocationName(topic.locationData));
    if (!title) return;

    dispatch({ type: 'FETCH_START', payload: { key: currentKey, keepPeriods: false } });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      loginMachine.recordUsage('llm');
      const idToken = await loginMachine.getIdToken();
      if (!idToken) throw new Error('user not authenticated');

      const params = new URLSearchParams({
        location: title,
        userLanguageLabel,
      });
      const url = `${functionUrls.getHistoricalPhasesMarkdownList}?${params.toString()}`;
      const resp = await fetch(url, {
        headers: {
          Accept: 'text/markdown',
          Authorization: `Bearer ${idToken}`,
        },
        signal: controller.signal,
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const text = await resp.text();

      const periods = parseHistoricalPeriodsMarkdown(text);

      // Cache and dispatch success
      periodCache.set(currentKey, periods);
      if (currentKey !== topicKeyRef.current) return;
      dispatch({ type: 'FETCH_SUCCESS', payload: { key: currentKey, periods } });
    } catch (err) {
      if (controller.signal.aborted) return; // Silent abort
      if (currentKey !== topicKeyRef.current) return;
      console.error('[HistoricalPeriods] Error fetching periods', err);
      
      // Check if it's an authentication error
      const isAuthError = err.message?.includes('not authenticated') || 
                         err.message?.includes('auth') ||
                         (err.response?.status === 401) ||
                         (err.response?.status === 403);
      
      if (isAuthError && isGuestAccount) {
        dispatch({ type: 'FETCH_ERROR', payload: { key: currentKey, error: 'AUTH_REQUIRED' } });
      } else {
        dispatch({ type: 'FETCH_ERROR', payload: { key: currentKey, error: 'could not connect to history service' } });
      }
    }
  }

  // Expose imperative API
  useImperativeHandle(ref, () => ({ fetchPeriods }));

  // Helper to compute narration title on click
  const getNarrationTopicTitle = () =>
    topic.type === 'article' ? topic.article.title : getLocationName(topic.locationData);

  /* -------------------   RENDER   ------------------- */
  // Still render container so heading keeps spacing, but show loader at very start

  const renderContent = () => {
    const currentKey = topicKeyRef.current;
    const isCurrentTopicState = state.key === currentKey;
    const visiblePeriods = isCurrentTopicState ? state.periods : [];

    if (state.status === 'loading' && isCurrentTopicState && !visiblePeriods.length) {
      return (
        <ThinkingIndicator />
      );
    }

    if (state.status === 'error' && isCurrentTopicState && !visiblePeriods.length) {
      if (state.error === 'AUTH_REQUIRED') {
        return (
          <div style={{ 
            padding: '15px 10px', 
            textAlign: 'center'
          }}>
            <button
              onClick={onSignInClick}
              style={{
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                width: '100%'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#16a34a'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#22c55e'}
            >
              Sign in to learn more
            </button>
          </div>
        );
      }
      
      return (
        <div style={{ padding: '10px', textAlign: 'left', color: '#666' }}>
          {state.error}
        </div>
      );
    }

    if (!visiblePeriods.length) {
      return (
        <div style={{ padding: '10px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={() => fetchPeriods(currentKey)}
              className="period-btn"
              style={{ width: '100%', textAlign: 'left' }}
            >
              Research
            </button>
          </div>
        </div>
      );
    }

    return visiblePeriods.map((p) => (
      <button
        key={`${p.title}-${p.date}`}
        className="period-btn"
        onClick={() => {
          const narrationTitle = getNarrationTopicTitle();
          const currentArticle = topic.type === 'article' ? topic.article : null;
          startNarration(
            narrationTitle,
            'historicPeriod',
            { period: `${p.title} (${p.date})`, topicId: topic.id },
            locationData,
            currentArticle
          );
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <strong>{p.title}</strong>
            <span className="period-date">{p.date}</span>
          </div>
          <i className="fas fa-volume-up" style={{ color: '#6b7280', fontSize: '1rem', opacity: '0.5' }}></i>
        </div>
      </button>
    ));
  };

  return <>{renderContent()}</>;
});

export default HistoricalPeriods;
